<template>
  <div>
    <card title="Reports" :isFullPage="true">
      <div class="d-flex justify-content-between">
        <div class="d-flex align-items-end">
          <download-button v-if="hasSelectedRows" :url="downloadUrl" fileName="report.pdf">
            Download <font-awesome-icon icon="download" />
          </download-button>
        </div>
        <div class="d-flex justify-content-end">
          <fg-input
            type="text"
            placeholder="Search"
            v-model="query"
            @keyupEnter="search"
            @clickedIcon="search"
            addonRightIcon="search"
            :iconClasses="['clickable']"
            style="max-width: 300px;"
          >
          </fg-input>
          <ul class="pagination b-pagination ziperase-toolbar">
            <li role="button" aria-hidden="true" class="page-item d-flex cursor-pointer" @click="showFilterModal">
              <span role="menuitem" aria-disabled="true" class="page-link no-wrap d-flex align-self-center">
                Filter <font-awesome-icon icon="filter" class="ml-1"/>
              </span>
            </li>
          </ul>
        </div>

      </div>
      <pagination-display
        v-if="deviceReports"
        :skip="skip"
        :count="count"
        :perPage="perPage"
        :lastIndex="lastIndex"
        @updatePerPage="updatePerPage"
      />
      <filter-display :filter="filter" :filterTypes="filterTypes" @clearFilter="clearFilter" />
      <selection-display
        v-show="selectAll"
        :selectAllPages="selectAllPages"
        :count="count"
        :selectedCount="selectedCount"
        @clearSelection="clearSelection"
        @toggleSelection="toggleSelectOnAllPages"
      />
      <b-table
        id="device-link-reports-table"
        :fields="filteredTableFields"
        :items="deviceReports"
        show-empty
        :busy="isLoading"
        @sort-changed="sortingChanged"
        :sort-by="sortBy"
        :sort-desc.sync="sortDesc"
        :no-local-sorting="true"
        ref="table"
        selectable
        no-select-on-click
      >
        <template v-slot:table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle mr-3"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>
        <template v-slot:head(select)>
          <div class="form-check device-select">
            <input type="checkbox" v-model="selectAll" @change="toggleSelection" class="form-check-input">
          </div>
        </template>
        <template v-slot:cell(select)="row">
          <label class="checkbox-wrapper">
            <input type="checkbox" :disabled="selectAllPages" :value="selectedRows.includes(row.item.id)"
              :checked="selectedRows.includes(row.item.id)" @input="selectRow(row)">
          </label>
        </template>
        <template v-slot:cell(view)="row">
          <router-link
            :to="`/device-link/reports/${row.item.id}`"
            class="page-link text-info text-center"
          >
            <font-awesome-icon icon="desktop" />
          </router-link>
        </template>

        <template v-slot:cell(created_at)="row">{{
          row.item.created_at + "+00:00" | prettyDateTime
        }}</template>
      </b-table>
      <b-pagination
        v-model="currentPage"
        @change="changePage"
        :total-rows="count"
        :per-page="perPage"
      >
      </b-pagination>
    </card>
    <filter-modal @filterItems="filterReports" title="Filter Reports" itemName="reports" :filterTypes="filterTypes" :initialFilter="filterConfig" />
  </div>
</template>

<script>
import deviceReportFilters from '../../constants/deviceReportFilters';
import config from "../../constants/config";
import Card from "../Cards/Card";
import FilterModal from '../Modals/FilterModal.vue';
import PaginationDisplay from "./PaginationDisplay";
import FilterDisplay from "./FilterDisplay";
import { mapGetters } from "vuex";
import DownloadButton from '../DownloadButton.vue';
import SelectionDisplay from './SelectionDisplay.vue';

export default {
  name: "DeviceLinkReportsTable",
  components: {
    Card,
    PaginationDisplay,
    FilterDisplay,
    FilterModal,
    DownloadButton,
    SelectionDisplay
  },
  data() {
    return {
      isLoading: false,
      currentPage: 1,
      query: '',
      selectedRows: [],
      selectAll: false,
      selectAllPages: false
    };
  },
  methods: {
    async fetchDeviceLinkReports(page) {
      if (page) {
        this.currentPage = page;
      }

      this.isLoading = true;
      await this.$store.dispatch("deviceReport/fetchDeviceReports", {
        skip: this.skip,
        limit: this.perPage,
      });

      if (this.selectAllPages) {
        this.toggleSelection(); // update which rows are showing as selected
      }

      this.isLoading = false;
    },
    async changePage(page) {
      await this.fetchDeviceLinkReports(page);
    },
    updatePerPage(perPage) {
      this.perPage = perPage;
      this.fetchDeviceLinkReports(1);
    },
    sortingChanged(context) {
      this.sortBy = context.sortBy;
      this.sortDesc = context.sortDesc;
      this.fetchDeviceLinkReports();
    },
    search() {
      console.log(`Settings searchQuery to value = ${this.query}`)
      this.$store.commit("deviceReport/searchQuery", this.query);
      this.fetchDeviceLinkReports(1);
    },
    clearSearch() {
      this.$store.commit('deviceReport/searchQuery', '');
      this.fetchDeviceLinkReports(1);
    },
    showFilterModal() {
      this.$bvModal.show(config.FILTER_MODAL_ID);
    },
    filterReports({ filterConfig, apiFilters }) {
      this.$store.commit('deviceReport/filterConfig', filterConfig);
      this.$store.commit('deviceReport/filter', apiFilters);
      this.fetchDeviceLinkReports(1);
    },
    clearFilter() {
      this.$store.commit('deviceReport/filterConfig', null);
      this.$store.commit('deviceReport/filter', null);
      this.fetchDeviceLinkReports(1);
    },
    selectRow(row) {
      let id = row.item.id;
      if (this.selectedRows.includes(id)) {
        const arrayIndex = this.selectedRows.indexOf(id);
        this.selectedRows.splice(arrayIndex, 1);
        this.$refs.table.unselectRow(row.index);

        this.selectAll = false;
        this.toggleSelectOnAllPages(false);
      } else {
        this.selectedRows.push(id);
        this.$refs.table.selectRow(row.index);
      }
    },
    clearSelection() {
      this.selectAll = false;
      this.toggleSelection();
    },
    toggleSelection() {
      if (this.selectAll) {
        this.selectedRows = this.deviceReports.map(report => report.id);
        this.$refs.table.selectAllRows();
      } else {
        this.selectedRows = [];
        this.$refs.table.clearSelected();
        this.toggleSelectOnAllPages(false);
      }
    },
    toggleSelectOnAllPages(select) {
      this.selectAllPages = select;
    }
  },
  computed: {
    ...mapGetters("user", ["isSuperUser", "isStaffUser", "isPartner"]),
    ...mapGetters("deviceReport", [
      "deviceReports",
      "count",
      "tableFields",
      "selectedCount",
      "searchQuery",
      "filter",
      "filterConfig",
      "fetchParams"
    ]),
    filteredTableFields() {
      if (this.isStaffUser || this.isSuperUser) {
        return this.tableFields;
      }
      else {
        return this.tableFields.filter(item => item.key !== "organization.name");
      }
    },
    sortBy: {
      get() {
        return this.$store.state.deviceReport.sortBy;
      },
      set(value) {
        this.$store.commit("deviceReport/sortBy", value);
      },
    },
    sortDesc: {
      get() {
        return this.$store.state.deviceReport.sortDesc;
      },
      set(value) {
        this.$store.commit("deviceReport/sortDesc", value);
      },
    },
    perPage: {
      get() {
        return this.$store.state.deviceReport.perPage;
      },
      set(value) {
        this.$store.commit("deviceReport/perPage", value);
      },
    },
    skip() {
      return (this.currentPage - 1) * this.perPage;
    },
    hasSelection() {
      return this.selectedCount > 0;
    },
    lastIndex() {
      return this.skip + this.deviceReports.length;
    },
    isDefaultTable() {
      return true;
    },
    filterTypes() {
      // remove organisation filter for not super users
      return deviceReportFilters.filter((filter) => {
        return this.isSuperUser || this.isStaffUser || this.isPartner || filter.key != 'organization_key';
      });
    },
    downloadUrl() {
      const params = new URLSearchParams();
      params.append('format', 'pdf');

      if (this.selectAllPages) {
        params.append('skip', 0);
        params.append('limit', this.count);

        Object.keys(this.fetchParams).forEach(key => params.append(key, this.fetchParams[key]));
        return `${process.env.VUE_APP_API_URL}/device-reports?${params.toString()}`
      } else {
        this.selectedRows.forEach(id => params.append('report_ids', id));
        return `${process.env.VUE_APP_API_URL}/device-reports/selection?${params.toString()}`
      }
    },
    hasSelectedRows() {
      return this.selectedRows.length > 0;
    },
    selectedCount() {
      return this.selectAllPages ? this.count : this.selectedRows.length;
    }
  },
};
</script>

<style>
.scroll {
  overflow-x: scroll;
}

.subtable {
  max-width: 75vw;
  overflow: scroll;
}

.table-bar {
  margin-bottom: 0px !important;
  padding-bottom: 0px !important;
}

.ziperase-toolbar {
  margin-bottom: 15px;
  margin-left: 10px;
}

.row-select {
  margin-top: -12px;
}

th {
  font-size: 1.0em !important;
}

.no-wrap {
  flex: 0 0 auto;
}

.checkbox-wrapper {
  cursor: pointer;
}

.device-select {
  margin-top: -12px;
}
</style>
